// NewProject - Project Creation Wizard
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import './NewProject.css';

const NewProject = () => {
    const { user } = useAuth();
    const navigate = useNavigate();

    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Form data
    const [projectData, setProjectData] = useState({
        name: '',
        description: '',
        ideaDescription: '',
        phases: [],
        generateWithAI: false
    });

    const totalSteps = 4;

    const updateData = (key, value) => {
        setProjectData(prev => ({ ...prev, [key]: value }));
    };

    // Generate phases with AI (placeholder for Gemini integration)
    const generatePhasesWithAI = async () => {
        setLoading(true);

        // Placeholder - will be replaced with Gemini API call
        // For now, generate some default phases based on project type
        const defaultPhases = [
            {
                name: 'Fase 1: Diseño y Planificación',
                tasks: [
                    { name: 'Definir arquitectura', status: 'pending' },
                    { name: 'Diseñar UI/UX', status: 'pending' },
                    { name: 'Crear documentación', status: 'pending' }
                ]
            },
            {
                name: 'Fase 2: Desarrollo Base',
                tasks: [
                    { name: 'Configurar proyecto', status: 'pending' },
                    { name: 'Implementar componentes base', status: 'pending' },
                    { name: 'Integrar base de datos', status: 'pending' }
                ]
            },
            {
                name: 'Fase 3: Funcionalidades Core',
                tasks: [
                    { name: 'Desarrollar features principales', status: 'pending' },
                    { name: 'Añadir autenticación', status: 'pending' },
                    { name: 'Testing inicial', status: 'pending' }
                ]
            },
            {
                name: 'Fase 4: Testing y Deployment',
                tasks: [
                    { name: 'Tests E2E', status: 'pending' },
                    { name: 'Optimización', status: 'pending' },
                    { name: 'Deploy a producción', status: 'pending' }
                ]
            }
        ];

        // Simulate AI processing delay
        await new Promise(resolve => setTimeout(resolve, 1500));

        updateData('phases', defaultPhases);
        setLoading(false);
    };

    // Add manual phase
    const addPhase = () => {
        updateData('phases', [
            ...projectData.phases,
            { name: `Nueva Fase ${projectData.phases.length + 1}`, tasks: [] }
        ]);
    };

    // Remove phase
    const removePhase = (index) => {
        const newPhases = [...projectData.phases];
        newPhases.splice(index, 1);
        updateData('phases', newPhases);
    };

    // Update phase name
    const updatePhaseName = (index, name) => {
        const newPhases = [...projectData.phases];
        newPhases[index].name = name;
        updateData('phases', newPhases);
    };

    // Create project
    const createProject = async () => {
        setLoading(true);
        setError('');

        try {
            // Insert project
            const { data: project, error: projectError } = await supabase
                .from('projects')
                .insert({
                    user_id: user.id,
                    name: projectData.name,
                    description: projectData.description,
                    progress: 0
                })
                .select()
                .single();

            if (projectError) throw projectError;

            // Insert phases
            for (let i = 0; i < projectData.phases.length; i++) {
                const phase = projectData.phases[i];

                const { data: phaseData, error: phaseError } = await supabase
                    .from('phases')
                    .insert({
                        project_id: project.id,
                        name: phase.name,
                        order_index: i
                    })
                    .select()
                    .single();

                if (phaseError) throw phaseError;

                // Insert tasks for this phase
                if (phase.tasks && phase.tasks.length > 0) {
                    const tasksToInsert = phase.tasks.map((task, j) => ({
                        id: `${project.id}-${i}-${j}`,
                        phase_id: phaseData.id,
                        name: task.name,
                        status: task.status || 'pending',
                        order_index: j
                    }));

                    const { error: tasksError } = await supabase
                        .from('tasks')
                        .insert(tasksToInsert);

                    if (tasksError) throw tasksError;
                }
            }

            // Navigate to new project
            navigate(`/project/${project.id}`);
        } catch (err) {
            console.error('Error creating project:', err);
            setError(err.message || 'Error al crear el proyecto');
        } finally {
            setLoading(false);
        }
    };

    const renderStep = () => {
        switch (step) {
            case 1:
                return (
                    <div className="wizard-step">
                        <h2>💡 Describe tu idea</h2>
                        <p>Cuéntanos sobre tu proyecto. Cuanto más detallado, mejor será el roadmap generado.</p>

                        <div className="form-group">
                            <label>¿Qué quieres construir?</label>
                            <textarea
                                value={projectData.ideaDescription}
                                onChange={(e) => updateData('ideaDescription', e.target.value)}
                                placeholder="Ej: Una aplicación móvil para gestionar inventario de una tienda, con escaneo de códigos de barras, alertas de stock bajo, y reportes mensuales..."
                                rows={6}
                            />
                        </div>

                        <div className="wizard-options">
                            <button
                                className="btn-ai"
                                onClick={() => { updateData('generateWithAI', true); setStep(2); generatePhasesWithAI(); }}
                                disabled={!projectData.ideaDescription.trim()}
                            >
                                🤖 Generar con IA
                            </button>
                            <button
                                className="btn-manual"
                                onClick={() => { updateData('generateWithAI', false); setStep(2); }}
                            >
                                ✏️ Añadir manualmente
                            </button>
                        </div>
                    </div>
                );

            case 2:
                return (
                    <div className="wizard-step">
                        <h2>📋 Define las fases</h2>
                        {loading ? (
                            <div className="ai-loading">
                                <div className="loading-spinner"></div>
                                <p>Generando fases con IA...</p>
                            </div>
                        ) : (
                            <>
                                <div className="phases-list">
                                    {projectData.phases.map((phase, index) => (
                                        <div key={index} className="phase-item">
                                            <input
                                                type="text"
                                                value={phase.name}
                                                onChange={(e) => updatePhaseName(index, e.target.value)}
                                                className="phase-input"
                                            />
                                            <span className="phase-tasks-count">
                                                {phase.tasks?.length || 0} tareas
                                            </span>
                                            <button
                                                className="btn-remove"
                                                onClick={() => removePhase(index)}
                                            >
                                                🗑️
                                            </button>
                                        </div>
                                    ))}
                                </div>

                                <button className="btn-add-phase" onClick={addPhase}>
                                    ➕ Añadir Fase
                                </button>
                            </>
                        )}
                    </div>
                );

            case 3:
                return (
                    <div className="wizard-step">
                        <h2>📝 Nombre del proyecto</h2>

                        <div className="form-group">
                            <label>Nombre</label>
                            <input
                                type="text"
                                value={projectData.name}
                                onChange={(e) => updateData('name', e.target.value)}
                                placeholder="Mi Proyecto Increíble"
                            />
                        </div>

                        <div className="form-group">
                            <label>Descripción (opcional)</label>
                            <textarea
                                value={projectData.description}
                                onChange={(e) => updateData('description', e.target.value)}
                                placeholder="Una breve descripción de tu proyecto..."
                                rows={3}
                            />
                        </div>
                    </div>
                );

            case 4:
                return (
                    <div className="wizard-step">
                        <h2>🚀 Resumen</h2>

                        <div className="summary-box">
                            <div className="summary-item">
                                <span className="summary-label">Proyecto:</span>
                                <span className="summary-value">{projectData.name || 'Sin nombre'}</span>
                            </div>
                            <div className="summary-item">
                                <span className="summary-label">Fases:</span>
                                <span className="summary-value">{projectData.phases.length}</span>
                            </div>
                            <div className="summary-item">
                                <span className="summary-label">Tareas:</span>
                                <span className="summary-value">
                                    {projectData.phases.reduce((sum, p) => sum + (p.tasks?.length || 0), 0)}
                                </span>
                            </div>
                        </div>

                        {error && (
                            <div className="wizard-error">
                                ⚠️ {error}
                            </div>
                        )}
                    </div>
                );

            default:
                return null;
        }
    };

    const canProceed = () => {
        switch (step) {
            case 1: return projectData.ideaDescription.trim().length > 0;
            case 2: return projectData.phases.length > 0 && !loading;
            case 3: return projectData.name.trim().length > 0;
            case 4: return true;
            default: return false;
        }
    };

    return (
        <div className="new-project-container">
            <div className="new-project-bg">
                <div className="grid-overlay"></div>
                <div className="glow-orb orb-1"></div>
                <div className="glow-orb orb-2"></div>
            </div>

            <nav className="wizard-nav">
                <Link to="/dashboard" className="back-link">
                    ← Volver al Dashboard
                </Link>
            </nav>

            <main className="wizard-main">
                {/* ¿Primera vez? - Solo en esta página, no en el dashboard */}
                <div className="new-project-first-time">
                    <div className="first-time-icon">📚</div>
                    <div className="first-time-text">
                        <strong>¿Primera vez?</strong>
                        <p>Tenemos una guía completa donde te explicamos cada herramienta sin tecnicismos. Creemos en ti y en tu capacidad de crear.</p>
                    </div>
                    <div className="first-time-actions">
                        <Link to="/guide" className="first-time-btn guide">Ver guía</Link>
                        <Link to="/start" className="first-time-btn start">Empezar configuración →</Link>
                    </div>
                </div>

                <div className="wizard-header">
                    <h1>✨ Nuevo Proyecto</h1>
                    <div className="wizard-progress">
                        {[1, 2, 3, 4].map(s => (
                            <div
                                key={s}
                                className={`progress-step ${s === step ? 'active' : ''} ${s < step ? 'completed' : ''}`}
                            >
                                {s < step ? '✓' : s}
                            </div>
                        ))}
                    </div>
                </div>

                <div className="wizard-content">
                    {renderStep()}
                </div>

                <div className="wizard-actions">
                    {step > 1 && (
                        <button
                            className="btn-back"
                            onClick={() => setStep(step - 1)}
                            disabled={loading}
                        >
                            ← Atrás
                        </button>
                    )}

                    {step < totalSteps ? (
                        <button
                            className="btn-next"
                            onClick={() => setStep(step + 1)}
                            disabled={!canProceed()}
                        >
                            Siguiente →
                        </button>
                    ) : (
                        <button
                            className="btn-create"
                            onClick={createProject}
                            disabled={loading || !projectData.name.trim()}
                        >
                            {loading ? 'Creando...' : '🚀 Crear Proyecto'}
                        </button>
                    )}
                </div>
            </main>
        </div>
    );
};

export default NewProject;
